import { useState } from 'react';

import { BackrsIcon, appColors } from '@backrs_ui/common-ui';

import { BaseFileUpload } from '@backrs_ui/common-ui/src/molecules/FileUpload/BaseFileUpload';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { axiosInstance as axios } from '@backrs_ui/grow-ui/src/core/axios';
import { DocumentType, DOCUMENT_TYPES } from '@backrs_ui/grow-ui/src/models/models';
import { ENVIRONMENT_URL } from '@backrs_ui/grow-ui/src/settings';
import { fetchWrapper as fetch } from '@backrs_ui/grow-ui/src/utils/fetch/fetchWrapper';
import { getBackrsToken } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';

import {
  SxProps,
  Theme,
  Box,
  Button,
  Typography,
  TextField,
  CircularProgress,
} from '@mui/material';
import { keyframes } from '@mui/system';
import axiosBase from 'axios';

import { EnterButton } from './EnterButton';
import {
  FILE_CONFIG,
  base64ToFile,
  extractFileIdFromDriveLink,
  getFileType,
  FileUploadError,
} from './fileUpload.util';

const { fileUpload } = appStrings.modules.chatBox;

type FileState = Readonly<{
  name: string;
  file: File | null;
}>;

type DocumentParsingState = Readonly<{
  text: string;
  documentType: DocumentType | null;
  mediaId?: number;
  s3Key?: string;
}>;

type FileUploadProps = Readonly<{
  userId: number;
  onSubmit: (fileName: string, content?: string) => void;
  onError?: (errorCode?: FileUploadError) => void;
  cancelComponent?: React.ReactNode;
  addToProfile?: boolean;
  documentType?: DocumentType;
}>;

export const FileUpload = ({
  userId,
  onSubmit,
  onError,
  cancelComponent,
  addToProfile,
  documentType,
}: FileUploadProps) => {
  const [driveLink, setDriveLink] = useState('');
  const [isLinkValid, setIsLinkValid] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<FileState>({ name: '', file: null });
  const [isLoading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAttachingToProfile, setIsAttachingToProfile] = useState(false);
  const [parsedDocument, setParsedDocument] = useState<DocumentParsingState>({
    text: '',
    documentType: null,
    mediaId: undefined,
    s3Key: undefined,
  });

  const accessToken = getBackrsToken();

  const allowedExtensions = Object.values(FILE_CONFIG).flatMap((config) =>
    config.extension.split(','),
  );

  const isValidDriveLink = (link: string): boolean => {
    if (!link.trim()) return false;

    const isDriveUrl = link.includes('drive.google.com');
    const isDocsUrl = link.includes('docs.google.com');

    if (!isDriveUrl && !isDocsUrl) return false;

    // check if we can extract a file ID from the link, else we can't use it.
    const isValid = extractFileIdFromDriveLink(link) !== null;

    setIsLinkValid(isValid);

    return isValid;
  };

  const uploadDocumentToMedia = async (file: File) => {
    const mediaPayload = {
      type: getFileType(file.type),
      name: file.name,
      size: file.size.toString(),
    };

    const mediaResponse = await fetch(`${ENVIRONMENT_URL}/media`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(mediaPayload),
    });

    if (!mediaResponse.ok) {
      throw new Error('Failed to get upload URL');
    }

    const mediaData = await mediaResponse.json();

    const uploadResponse = await fetch(mediaData.upload_url, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'application/pdf',
      },
      skipAuth: true,
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload file');
    }

    return {
      mediaId: mediaData.id,
      s3Key: mediaData.s3_key,
    };
  };

  const attachDocumentToProfile = async (
    file: File,
    docType: DocumentType | null,
    mediaId: number,
    attachToStory = true,
  ) => {
    if (attachToStory) {
      const profilePayload = {
        user_id: userId,
        category_id: 'story',
        visibility: 'panel', // assume we don't want it to default to public
        question_id: 'document',
        option_id: documentType ?? docType ?? 'document',
        title: file.name, // use the file name as the title since we don't ask for a title
        media_id: mediaId,
        media_type: getFileType(file.type),
      };

      const profileResponse = await fetch(`${ENVIRONMENT_URL}/users-profile-answers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(profilePayload),
      });

      if (!profileResponse.ok) {
        throw new Error('Failed to create profile answer');
      }
    }
  };

  const stringifyDocumentForLubav = async (file: File) => {
    // first upload the file to get the s3 key
    const { mediaId, s3Key } = await uploadDocumentToMedia(file);

    const formData = new FormData();

    formData.append('file', file);

    // then process the document
    const content = await axios.post('/api/process-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: {
        user_id: userId,
        s3_key: s3Key,
        ...(!documentType ? { classify_as: Object.values(DOCUMENT_TYPES) } : {}),
      },
      paramsSerializer: {
        indexes: null,
      },
    });

    setParsedDocument({
      text: content.data.text,
      documentType: content.data.document_type || null,
      mediaId,
      s3Key,
    });
  };

  const resetState = () => {
    setUploadedFile({ name: '', file: null });
    setIsUploading(false);
    setParsedDocument({
      text: '',
      documentType: null,
      mediaId: undefined,
      s3Key: undefined,
    });
  };

  const processFile = async (file: File) => {
    if (!file) return;

    setUploadedFile({ name: file.name, file });
    setIsUploading(true);

    try {
      await stringifyDocumentForLubav(file);
    } catch (error) {
      console.error('Error processing document:', error);

      resetState();
      onError?.();
    } finally {
      setIsUploading(false);
    }
  };

  const processDriveLink = async (link: string) => {
    try {
      setIsUploading(true);

      const fileId = extractFileIdFromDriveLink(link);

      if (!fileId) {
        throw new Error('Invalid Google Drive link format');
      }

      // get the file from google drive
      const fileResponse = await axios.post('/api/google-drive-document-upload', null, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: {
          file_id: fileId,
        },
        paramsSerializer: {
          indexes: null,
        },
      });

      const { file } = fileResponse.data;

      if (!file) {
        throw new Error('Failed to get file from Google Drive');
      }

      const actualFile = base64ToFile(file);

      // upload the file and get the s3 key
      const { mediaId, s3Key } = await uploadDocumentToMedia(actualFile);

      const formData = new FormData();

      formData.append('file', actualFile);

      // then process the document
      const processedResponse = await axios.post('/api/process-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: {
          user_id: userId,
          s3_key: s3Key,
          ...(!documentType ? { classify_as: Object.values(DOCUMENT_TYPES) } : {}),
        },
        paramsSerializer: {
          indexes: null,
        },
      });

      return {
        file: actualFile,
        text: processedResponse.data.text,
        documentType: processedResponse.data.document_type || null,
        mediaId,
        s3Key,
      };
    } catch (error) {
      console.error('Error processing file from Google Drive:', error);
      resetState();

      if (axiosBase.isAxiosError(error) && error.response?.status === 403) {
        onError?.('UNAUTHORIZED');
      } else {
        onError?.('UNKNOWN_ERROR');
      }

      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const attachToProfileAndSubmit = async (
    file: File,
    text: string,
    docType: DocumentType | null,
    mediaId?: number,
  ) => {
    if (addToProfile && (docType || documentType) && file && mediaId) {
      setIsAttachingToProfile(true);
      try {
        await attachDocumentToProfile(file, docType, mediaId);
      } catch (error) {
        console.error('Error attaching document to profile:', error);
        onError?.();

        return false;
      } finally {
        setIsAttachingToProfile(false);
      }
    }

    onSubmit(file.name, text);

    return true;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    if (driveLink && !uploadedFile.file && isLinkValid) {
      const driveResult = await processDriveLink(driveLink);

      if (driveResult) {
        const { file, text, documentType: docType, mediaId } = driveResult;

        await attachToProfileAndSubmit(file, text, docType, mediaId);
      }

      return;
    }

    if (uploadedFile.name && uploadedFile.file && parsedDocument.text) {
      await attachToProfileAndSubmit(
        uploadedFile.file,
        parsedDocument.text,
        parsedDocument.documentType,
        parsedDocument.mediaId,
      );
    }

    setIsSubmitting(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleUploadButtonClick = () => {
    const fileInput = document.getElementById('file-upload') as HTMLInputElement | null;

    if (fileInput) {
      fileInput.click();
    }
  };

  return (
    <Box sx={fileUploadContainerStyle} onKeyPress={handleKeyPress}>
      <Box sx={fileUploadButtonContainerStyle}>
        <Button sx={fileUploadButtonStyle} onClick={handleUploadButtonClick}>
          <BackrsIcon name="IconUpload" alt="Upload" style={{ marginRight: '8px' }} />
          {uploadedFile.name || fileUpload.uploadAFile}
        </Button>
      </Box>

      <BaseFileUpload
        onFileSelect={processFile}
        onError={(error) => {
          console.error('Error from BaseFileUpload:', error);
          onError?.();
        }}
        acceptedFileTypes={allowedExtensions}
        containerSx={dragAndDropStyle}
      >
        {uploadedFile.name ? (
          <Typography sx={{ fontSize: '10pt' }}>
            {isLoading ? fileUpload.uploading : uploadedFile.name}
          </Typography>
        ) : (
          <Box>
            <BackrsIcon
              name="IconPlusWhiteBlueCircle"
              alt="Upload"
              style={{ marginRight: '8px' }}
            />
            <Typography
              color={(theme: Theme) => theme.backrs.colors['Beige/950']}
              sx={{ fontSize: '10.5pt' }}
            >
              {fileUpload.dragAndDrop}
            </Typography>
            <Typography sx={dragAndDropDescriptionStyle}>{fileUpload.acceptedFileTypes}</Typography>
          </Box>
        )}
      </BaseFileUpload>

      <Box sx={driveLinkContainerStyle}>
        <TextField
          fullWidth
          placeholder={fileUpload.uploadFromGoogleDrive}
          value={driveLink}
          onChange={(e) => {
            isValidDriveLink(e.target.value);
            setDriveLink(e.target.value);
          }}
          sx={driveLinkFieldStyle}
          disabled={!!uploadedFile.name || isSubmitting}
          error={driveLink.trim() !== '' && !isLinkValid}
          helperText={driveLink.trim() !== '' && !isLinkValid ? fileUpload.invalidLink : ''}
        />
      </Box>

      <Box sx={submitContainerStyle}>
        <Box sx={submitButtonContainerStyle}>
          <EnterButton
            onClick={handleSubmit}
            label={fileUpload.submit}
            disabled={
              (!uploadedFile.name && (!driveLink || !isLinkValid)) ||
              isLoading ||
              isAttachingToProfile ||
              isSubmitting
            }
            showEnterHint
          />
          {isSubmitting && <CircularProgress size={20} sx={animationStyle} />}
        </Box>
        {cancelComponent}
      </Box>
    </Box>
  );
};

const fileUploadContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: 2,
  animation: `${keyframes`
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `} 0.3s ease-out`,
} as const;

const dragAndDropStyle: SxProps<Theme> = {
  border: `2px dashed ${appColors.color.blue1}`,
  borderRadius: '8px',
  padding: '20px',
  height: '130px',
  alignItems: 'center',
  width: '100%',
  justifyContent: 'center',
  display: 'flex',
  textAlign: 'center',
  transition: 'background-color 0.2s ease',
  cursor: 'pointer',
  '&.dragover': {
    backgroundColor: appColors.color.blue4,
  },
  color: appColors.neutral.gray1,
} as const;

const dragAndDropDescriptionStyle: SxProps<Theme> = {
  fontSize: '8pt',
  color: appColors.neutral.gray1,
} as const;

const submitContainerStyle: SxProps<Theme> = {
  display: 'flex',
  gap: 2,
  alignItems: 'center',
  justifyContent: 'space-between',
} as const;

const submitButtonContainerStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
} as const;

const driveLinkContainerStyle: SxProps<Theme> = {
  width: '100%',
  marginTop: 1,
} as const;

const driveLinkFieldStyle: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '10.5pt',
  },
} as const;

const animationStyle: React.CSSProperties = {
  height: '20px',
  width: '20px',
} as const;

const fileUploadButtonContainerStyle: SxProps<Theme> = {
  display: 'flex',
  gap: 2,
  alignItems: 'center',
} as const;

const fileUploadButtonStyle: SxProps<Theme> = {
  padding: '6px 16px',
  borderRadius: '8px',
  backgroundColor: appColors.color.blue4,
  color: appColors.color.navy2,
  cursor: 'pointer',
  display: 'flex',
  fontSize: '11pt',
  fontWeight: '600',
  width: 'fit-content',
  justifyContent: 'center',
  textTransform: 'none',
} as const;
